import React from 'react';
import MuiDialog from '@mui/material/Dialog';
import { Box, CircularProgress, IconButton, Typography } from '@mui/material';
import { CloseCircleOutlined } from '@ant-design/icons';

type Props = {
  width?: number;
  containerClassName?: string;
  title: string;
  titleClassName?: string;
  /** Renders in the header left slot (e.g. edit). Title stays centered. */
  titleLeft?: React.ReactNode;
  open: boolean;
  handleCancel: () => void;
  isLoading?: boolean;
  footer?: React.ReactNode;
  /** When false, Escape does not close the dialog (matches MUI disableEscapeKeyDown). */
  keyboard?: boolean;
  closeIcon?: React.ReactNode;
  children: React.ReactNode;
};

export default function Dialog({
  width = 720,
  containerClassName,
  title,
  titleClassName,
  titleLeft,
  open,
  handleCancel,
  isLoading,
  footer,
  keyboard = true,
  closeIcon,
  children,
}: Props) {
  return (
    <MuiDialog
      open={open}
      onClose={handleCancel}
      maxWidth={false}
      disableEscapeKeyDown={keyboard === false}
      PaperProps={{
        className: containerClassName,
        sx: {
          width,
          maxWidth: '95vw',
          borderRadius: '24px',
          position: 'relative',
        },
      }}
    >
      <Box sx={{ position: 'relative', p: 4, pb: footer ? 2 : 4 }}>
        {isLoading && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: 'rgba(255,255,255,0.6)',
              zIndex: 2,
              borderRadius: 'inherit',
            }}
          >
            <CircularProgress />
          </Box>
        )}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            width: '100%',
            pb: 2,
          }}
        >
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              justifyContent: 'flex-start',
              alignItems: 'center',
              minHeight: 40,
            }}
          >
            {titleLeft}
          </Box>
          <Typography
            sx={{ flex: 1, textAlign: 'center' }}
            className={`fontWeight--bold fontColor--green fontSize--28 ${titleClassName ?? ''}`}
          >
            {title}
          </Typography>
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              justifyContent: 'flex-end',
              alignItems: 'center',
            }}
          >
            <IconButton onClick={handleCancel} sx={{ p: '6px' }}>
              {closeIcon || <CloseCircleOutlined style={{ fontSize: 30 }} />}
            </IconButton>
          </Box>
        </Box>
        {children}
        {footer}
      </Box>
    </MuiDialog>
  );
}
